function getPlacement (modifiers) {
  if (modifiers.right) return 'right'
  if (modifiers.bottom || modifiers.down) return 'bottom'
  if (modifiers.left) return 'left'
  if (modifiers.up) return 'top'
  return 'top'
}

function getTrigger (modifiers = {}) {
  if (modifiers.click) return 'click'
  if (modifiers.focus) return 'focus'
  if (modifiers.hover) return 'hover'
  return 'hover'
}

function isEnterable (modifiers = {}) {
  return !modifiers.unenterable
}

function getContent (value) {
  if (value && typeof value === 'object' && Object.prototype.hasOwnProperty.call(value, 'content')) {
    return value.content != null ? String(value.content) : ''
  }
  if (value != null) {
    return String(value)
  }
  return ''
}

function getOppositePlacement (placement) {
  const oppositePlacement = {
    top: 'bottom',
    bottom: 'top',
    left: 'right',
    right: 'left'
  }

  return oppositePlacement[placement] || 'top'
}

function calculatePosition (rect, popoverRect, placement, gap) {
  switch (placement) {
    case 'top':
      return {
        top: rect.top - popoverRect.height - gap,
        left: rect.left + (rect.width - popoverRect.width) / 2
      }
    case 'bottom':
      return {
        top: rect.bottom + gap,
        left: rect.left + (rect.width - popoverRect.width) / 2
      }
    case 'left':
      return {
        top: rect.top + (rect.height - popoverRect.height) / 2,
        left: rect.left - popoverRect.width - gap
      }
    case 'right':
    default:
      return {
        top: rect.top + (rect.height - popoverRect.height) / 2,
        left: rect.right + gap
      }
  }
}

function overflowsViewport (position, popoverRect, padding) {
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight

  return (
    position.left < padding ||
    position.left + popoverRect.width > viewportWidth - padding ||
    position.top < padding ||
    position.top + popoverRect.height > viewportHeight - padding
  )
}

function clampPosition (position, popoverRect, padding) {
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight

  return {
    top: Math.min(
      Math.max(position.top, padding),
      Math.max(padding, viewportHeight - popoverRect.height - padding)
    ),
    left: Math.min(
      Math.max(position.left, padding),
      Math.max(padding, viewportWidth - popoverRect.width - padding)
    )
  }
}

function positionPopover (el, popoverEl, preferredPlacement) {
  const rect = el.getBoundingClientRect()

  popoverEl.style.top = '0px'
  popoverEl.style.left = '0px'
  const popoverRect = popoverEl.getBoundingClientRect()

  const gap = 10
  const padding = 8

  let finalPlacement = preferredPlacement
  let position = calculatePosition(rect, popoverRect, finalPlacement, gap)

  if (overflowsViewport(position, popoverRect, padding)) {
    const fallbackPlacement = getOppositePlacement(preferredPlacement)
    const fallbackPosition = calculatePosition(rect, popoverRect, fallbackPlacement, gap)

    finalPlacement = fallbackPlacement
    position = fallbackPosition
  }

  const clampedPosition = clampPosition(position, popoverRect, padding)

  popoverEl.className = `popover ${finalPlacement} in`
  popoverEl.style.top = clampedPosition.top + 'px'
  popoverEl.style.left = clampedPosition.left + 'px'
}

function createPopoverEl (content, placement) {
  const wrapper = document.createElement('div')
  wrapper.className = `popover ${placement} in`
  wrapper.setAttribute('role', 'tooltip')
  wrapper.style.display = 'block'
  wrapper.style.position = 'fixed'
  wrapper.style.zIndex = '2147483647'
  wrapper.style.pointerEvents = 'auto'

  const arrow = document.createElement('div')
  arrow.className = 'arrow'
  wrapper.appendChild(arrow)

  const contentEl = document.createElement('div')
  contentEl.className = 'popover-content'
  contentEl.textContent = content
  wrapper.appendChild(contentEl)

  return wrapper
}

function reposition (el) {
  if (!el) return
  if (el._pttPopoverEl) {
    positionPopover(el, el._pttPopoverEl, el._pttPopoverPlacement)
  }
}

function stopTrackingPosition (el) {
  if (!el) return
  if (el._pttPopoverAnimationFrameId) {
    cancelAnimationFrame(el._pttPopoverAnimationFrameId)
    el._pttPopoverAnimationFrameId = null
  }
}

function trackPosition (el) {
  if (!el) return
  stopTrackingPosition(el)

  const loop = () => {
    if (!el._pttPopoverEl) {
      el._pttPopoverAnimationFrameId = null
      return
    }

    reposition(el)
    el._pttPopoverAnimationFrameId = requestAnimationFrame(loop)
  }

  el._pttPopoverAnimationFrameId = requestAnimationFrame(loop)
}

function show (el) {
  if (!el) return
  hide(el)
  const content = el._pttPopoverContent
  if (!content) return

  const placement = el._pttPopoverPlacement || 'top'
  const popoverEl = createPopoverEl(content, placement)
  document.body.appendChild(popoverEl)
  el._pttPopoverEl = popoverEl

  if (el._pttPopoverEnterable) {
    popoverEl.addEventListener('mouseenter', el._pttPopoverClearHideHandler)
    popoverEl.addEventListener('mouseleave', el._pttPopoverHideHandler)
  }

  positionPopover(el, popoverEl, placement)
  trackPosition(el)
}

function hide (el) {
  if (!el) return
  stopTrackingPosition(el)

  if (el._pttPopoverHideTimeout) {
    clearTimeout(el._pttPopoverHideTimeout)
    el._pttPopoverHideTimeout = null
  }

  if (el._pttPopoverEl) {
    el._pttPopoverEl.remove()
    el._pttPopoverEl = null
  }
}

function scheduleHide (el) {
  if (!el) return
  if (el._pttPopoverHideTimeout) {
    clearTimeout(el._pttPopoverHideTimeout)
  }

  el._pttPopoverHideTimeout = setTimeout(() => {
    hide(el)
  }, el._pttPopoverTrigger === 'hover' ? 100 : 0)
}

function clearHideTimeout (el) {
  if (!el) return
  if (el._pttPopoverHideTimeout) {
    clearTimeout(el._pttPopoverHideTimeout)
    el._pttPopoverHideTimeout = null
  }
}

function onDocumentClick (el, event) {
  if (!el) return
  if (!el._pttPopoverEl || el._pttPopoverTrigger !== 'click') return
  if (el.contains(event.target)) return
  if (el._pttPopoverEl.contains(event.target)) return
  hide(el)
}

function setup (el, binding) {
  if (!el) return
  if (el._pttPopoverBound) return
  el._pttPopoverBound = true

  el._pttPopoverPlacement = getPlacement(binding.modifiers)
  el._pttPopoverTrigger = getTrigger(binding.modifiers)
  el._pttPopoverEnterable = isEnterable(binding.modifiers)
  el._pttPopoverContent = getContent(binding.value)

  el._pttPopoverShowHandler = () => show(el)
  el._pttPopoverHideHandler = () => scheduleHide(el)
  el._pttPopoverImmediateHideHandler = () => hide(el)
  el._pttPopoverClearHideHandler = () => clearHideTimeout(el)
  el._pttPopoverToggleHandler = () => {
    if (el._pttPopoverEl) hide(el)
    else show(el)
  }
  el._pttPopoverDocumentClickHandler = (event) => onDocumentClick(el, event)
  el._pttPopoverRepositionHandler = () => reposition(el)

  if (el._pttPopoverTrigger === 'click') {
    el.addEventListener('click', el._pttPopoverToggleHandler)
    document.addEventListener('click', el._pttPopoverDocumentClickHandler, true)
  } else if (el._pttPopoverTrigger === 'focus') {
    el.addEventListener('focus', el._pttPopoverShowHandler)
    el.addEventListener('blur', el._pttPopoverImmediateHideHandler)
  } else {
    el.addEventListener('mouseenter', el._pttPopoverShowHandler)
    el.addEventListener('mouseleave', el._pttPopoverHideHandler)
  }

  window.addEventListener('resize', el._pttPopoverRepositionHandler)
  window.addEventListener('scroll', el._pttPopoverRepositionHandler, true)
}

function teardown (el) {
  if (!el) return
  hide(el)
  el._pttPopoverBound = false
  el.removeEventListener('mouseenter', el._pttPopoverShowHandler)
  el.removeEventListener('mouseleave', el._pttPopoverHideHandler)
  el.removeEventListener('focus', el._pttPopoverShowHandler)
  el.removeEventListener('blur', el._pttPopoverImmediateHideHandler)
  el.removeEventListener('click', el._pttPopoverToggleHandler)
  document.removeEventListener('click', el._pttPopoverDocumentClickHandler, true)
  window.removeEventListener('resize', el._pttPopoverRepositionHandler)
  window.removeEventListener('scroll', el._pttPopoverRepositionHandler, true)
}

function onUpdate (el, binding) {
  if (!el) return
  el._pttPopoverContent = getContent(binding.value)
  el._pttPopoverPlacement = getPlacement(binding.modifiers)
  el._pttPopoverTrigger = getTrigger(binding.modifiers)
  el._pttPopoverEnterable = isEnterable(binding.modifiers)
  if (el._pttPopoverEl) {
    el._pttPopoverEl.querySelector('.popover-content').textContent = el._pttPopoverContent
    positionPopover(el, el._pttPopoverEl, el._pttPopoverPlacement)
  }
}

export const popover = {
  mounted: setup,
  updated: onUpdate,
  unmounted: teardown
}
